import { Divider, HairlineCard, Stack, Typography } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';

/** One answer as it was recorded. */
export interface StageRecordRow {
  label: string;
  /** `null`/empty prints an em dash — "not answered" is itself a fact. */
  value: string | null | undefined;
  /** Long prose (notes, observations) reads as a block rather than a right-aligned line. */
  block?: boolean;
}

/**
 * A completed stage, as a record rather than a form.
 *
 * A stage the file has moved past is history: the answers are what the officer
 * recorded at the time, and the screen should say so plainly. Rendering them as
 * live inputs invites edits that the API will reject and, worse, suggests the
 * record is still in play when the work has already been done on the strength
 * of it.
 */
export function StageRecord({ rows }: { rows: StageRecordRow[] }) {
  const { t } = useTranslation();
  const dash = '—';

  return (
    <HairlineCard sx={{ p: 2.5 }}>
      <Stack spacing={1.25} divider={<Divider flexItem />}>
        {rows.map((row, index) =>
          row.block ? (
            <Stack key={index} spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {row.value?.trim() ? row.value : dash}
              </Typography>
            </Stack>
          ) : (
            <Stack
              key={index}
              direction="row"
              spacing={2}
              sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'right' }}>
                {row.value?.trim() ? row.value : dash}
              </Typography>
            </Stack>
          ),
        )}
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('gov.stage.nothingRecorded')}
          </Typography>
        )}
      </Stack>
    </HairlineCard>
  );
}
